import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './supabase';
import { AuthPage } from './components/AuthPage';
import { UsernameForm } from './components/UsernameForm';
import { AdminPanel } from './components/AdminPanel';
import { Leaderboard } from './components/Leaderboard';
import { Leagues } from './components/Leagues';
import { FlutterHeartGame } from './games/FlutterHeart';
import { FlutterHeartCloud } from './games/FlutterHeartCloud';
import { FlutterRush } from './games/FlutterRush';
import { FlutterHoop } from './games/FlutterHoop';
import { FlutterHeart_Test } from './games/FlutterHeart_Test';
import { HeartCatcher } from './games/HeartCatcher';
import { FlutterCatch } from './games/FlutterCatch';

import './App.css';

function GameSelect() {
  const navigate = useNavigate();
  return (
    <div className="game-select-container">
      <h1 className="game-select-title">🩷 Flutter Heart</h1>
      <p className="game-select-subtitle">Pick a game to play</p>
      <div className="game-select-cards">
        <button className="game-select-card" onClick={() => navigate('/flutter')}>
          <span className="game-select-card-icon">🩷</span>
          <span className="game-select-card-name">Flutter Heart</span>
          <span className="game-select-card-desc">Dodge cloud pillars in a rosy sky</span>
        </button>
        <button className="game-select-card" onClick={() => navigate('/cloud')}>
          <span className="game-select-card-icon">☁️</span>
          <span className="game-select-card-name">Cloud Dodge</span>
          <span className="game-select-card-desc">Fly through gaps in a dreamy cloudscape</span>
        </button>
        <button className="game-select-card" onClick={() => navigate('/flutterrush')}>
          <span className="game-select-card-icon">🏃</span>
          <span className="game-select-card-name">Flutter Rush</span>
          <span className="game-select-card-desc">Run and jump over obstacles</span>
        </button>
        <button className="game-select-card" onClick={() => navigate('/heartcatcher')}>
          <span className="game-select-card-icon">💝</span>
          <span className="game-select-card-name">Heart Catcher (Beta) </span>
          <span className="game-select-card-desc">Catch falling hearts, dodge the broken ones</span>
        </button>
        <button className="game-select-card" onClick={() => navigate('/fluttercatch')}>
          <span className="game-select-card-icon">🫧</span>
          <span className="game-select-card-name">Flutter Catch (Beta) </span>
          <span className="game-select-card-desc">Tap popping hearts before they vanish — 30 seconds</span>
        </button>
      </div>
    </div>
  );
}

function GameWrapper({ GameComponent, title }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [page, setPage] = useState('auth');
  const location = useLocation();

  const fetchProfile = async (userId) => {
    try {
      const { data } = await supabase.from('profiles').select('*').eq('id', userId).single();
      return data;
    } catch {
      return null;
    }
  };

  const handleAuthSuccess = async (authUser) => {
    setUser(authUser);
    const p = await fetchProfile(authUser.id);
    setProfile(p);
    setPage(p ? 'game' : 'create-username');
  };

  const handleUsernameSaved = async () => {
    const p = await fetchProfile(user.id);
    setProfile(p);
    setPage('game');
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      if (data.session?.user) {
        const authUser = data.session.user;
        setUser(authUser);
        const p = await fetchProfile(authUser.id);
        setProfile(p);
        setPage(p ? 'game' : 'create-username');
      }
    }).catch(() => {});

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setUser(null);
        setProfile(null);
        setPage('auth');
      }
    });

    return () => { authListener?.subscription.unsubscribe(); };
  }, []);

  return (
    <div className="App">
      {page === 'auth' && (
        <AuthPage onAuthSuccess={handleAuthSuccess} title={title} />
      )}
      {page === 'create-username' && user && (
        <UsernameForm user={user} existingUsername={null} onSave={handleUsernameSaved} title={title} />
      )}
      {page === 'edit-profile' && user && (
        <UsernameForm
          user={user}
          existingUsername={profile?.username}
          onSave={handleUsernameSaved}
          onCancel={() => setPage('game')}
          title={title}
        />
      )}
      {page === 'admin' && user && profile?.is_admin && (
        <AdminPanel onBack={() => setPage('game')} />
      )}
      {page === 'game' && user && (
        <GameComponent user={user} profile={profile} onNavigate={setPage} />
      )}
      {page === 'leaderboard' && user && (
        <Leaderboard user={user} onBack={() => setPage('game')} onNavigate={setPage} gameId={location.pathname} />
      )}
      {page === 'leagues' && user && (
        <Leagues user={user} onBack={() => setPage('game')} gameId={location.pathname} />
      )}
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<GameSelect />} />
      <Route path="/flutter" element={<GameWrapper GameComponent={FlutterHeartGame} title="🩷Flutter Heart" />} />
      <Route path="/cloud" element={<GameWrapper GameComponent={FlutterHeartCloud} title="🩷 Flutter Heart Cloud ☁️ " />} />
      <Route path="/flutterrush" element={<GameWrapper GameComponent={FlutterRush} title="Flutter Rush 🏃" />} />
      <Route path="/flutterhoop" element={<GameWrapper GameComponent={FlutterHoop} title="🌀 Flutter Hoop" />} />
      <Route path="/heartcatcher"  element={<GameWrapper GameComponent={HeartCatcher}  title="💝 Heart Catcher (Beta)" />} />
      <Route path="/fluttercatch"  element={<GameWrapper GameComponent={FlutterCatch}  title="🫧 Flutter Catch (Beta)"  />} />
      <Route path="/flutter_Test" element={<GameWrapper GameComponent={FlutterHeart_Test} title="🩷 Flutter Test" />} />
      
    </Routes>
  );
}